import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthShell, inputCls } from "@/pages/Login";

export default function CreateWedding() {
  const [sp] = useSearchParams();
  const [pkgs, setPkgs] = useState([]);
  const [f, setF] = useState({ partner1: "", partner2: "", wedding_date: "", package_id: sp.get("paket") || "digital", email: "", password: "", accept_terms: false, table_count: 10 });
  const [ship, setShip] = useState({ name: "", address: "", postal_code: "", city: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const nav = useNavigate();
  useEffect(() => { api.get("/public/packages").then((r) => setPkgs(r.data)); }, []);

  const pkg = pkgs.find((p) => p.id === f.package_id);
  const perTable = pkg?.per_table_price || 0;
  const total = pkg ? pkg.price + perTable * (Number(f.table_count) || 0) : null;
  const daysLeft = useMemo(() => (f.wedding_date ? Math.ceil((new Date(f.wedding_date) - new Date()) / 86400000) : null), [f.wedding_date]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register-wedding", { ...f, table_count: perTable ? Number(f.table_count) : 0, shipping: pkg?.physical ? ship : null });
      setUser(data.user);
      toast.success("Poroka je ustvarjena!");
      nav(`/nadzorna-plosca/${data.wedding_id}`);
    } catch (er) { toast.error(errMsg(er)); }
    setBusy(false);
  };

  return (
    <AuthShell title="Ustvarita svojo poroko" subtitle="Nekaj podatkov in vajina galerija je pripravljena.">
      <form onSubmit={submit} className="space-y-4" data-testid="create-wedding-form">
        <div className="grid grid-cols-2 gap-3">
          <input className={inputCls} placeholder="Ime partnerja" required value={f.partner1} onChange={set("partner1")} data-testid="create-partner1-input" />
          <input className={inputCls} placeholder="Ime partnerke" required value={f.partner2} onChange={set("partner2")} data-testid="create-partner2-input" />
        </div>
        <label className="block text-sm text-[#6C6560]">Datum poroke
          <input className={`${inputCls} mt-1`} type="date" required value={f.wedding_date} onChange={set("wedding_date")} data-testid="create-date-input" />
        </label>
        <div className="grid grid-cols-3 gap-2" data-testid="create-package-options">
          {pkgs.map((p) => (
            <button type="button" key={p.id} onClick={() => setF({ ...f, package_id: p.id })} className={`p-3 rounded-xl border text-left transition-colors ${f.package_id === p.id ? "border-[#231F20] bg-[#FFFDF9]" : "border-[#DFD5C5]"}`} data-testid={`create-package-${p.id}`}>
              <span className="block font-display text-xl">{p.name}</span><span className="text-sm text-[#6C6560]">{p.price} €{p.per_table_price ? ` + ${p.per_table_price} €/miza` : ""}</span>
            </button>
          ))}
        </div>
        {perTable > 0 && (
          <label className="block text-sm text-[#6C6560]">Število miz (ena QR kartica na mizo)
            <input className={`${inputCls} mt-1`} type="number" min="1" max="80" required value={f.table_count} onChange={set("table_count")} data-testid="create-tables-input" />
          </label>
        )}
        {pkg?.physical && (
          <div className="space-y-3 p-4 rounded-2xl bg-[#F3ECE1]" data-testid="create-shipping-fields">
            <p className="text-sm font-medium">Naslov za dostavo fizičnih izdelkov</p>
            {daysLeft !== null && daysLeft < 21 && (
              <p className="text-xs text-[#8A5A2B] flex gap-2" data-testid="create-delivery-warning"><AlertTriangle size={16} className="shrink-0" /> Do poroke je manj kot 21 dni. Izdelava traja 7–10 dni, dostava 5–10 dni, zato pravočasne dostave ne moremo zagotoviti.</p>
            )}
            <input className={inputCls} placeholder="Ime in priimek prejemnika" required value={ship.name} onChange={(e) => setShip({ ...ship, name: e.target.value })} data-testid="ship-name-input" />
            <input className={inputCls} placeholder="Ulica in hišna številka" required value={ship.address} onChange={(e) => setShip({ ...ship, address: e.target.value })} data-testid="ship-address-input" />
            <div className="grid grid-cols-3 gap-2">
              <input className={inputCls} placeholder="Pošta" required value={ship.postal_code} onChange={(e) => setShip({ ...ship, postal_code: e.target.value })} data-testid="ship-postal-input" />
              <input className={`${inputCls} col-span-2`} placeholder="Kraj" required value={ship.city} onChange={(e) => setShip({ ...ship, city: e.target.value })} data-testid="ship-city-input" />
            </div>
            <input className={inputCls} placeholder="Telefon za dostavo (neobvezno)" value={ship.phone} onChange={(e) => setShip({ ...ship, phone: e.target.value })} data-testid="ship-phone-input" />
          </div>
        )}
        <input className={inputCls} type="email" placeholder="E-poštni naslov" required value={f.email} onChange={set("email")} data-testid="create-email-input" />
        <input className={inputCls} type="password" placeholder="Geslo (vsaj 8 znakov)" required minLength={8} value={f.password} onChange={set("password")} data-testid="create-password-input" />
        <label className="flex gap-3 text-sm text-[#6C6560] items-start">
          <input type="checkbox" checked={f.accept_terms} onChange={set("accept_terms")} required className="mt-1 accent-[#231F20]" data-testid="create-terms-checkbox" />
          <span>Strinjava se s <Link to="/pravno/pogoji" target="_blank" className="underline">splošnimi pogoji</Link> in <Link to="/pravno/zasebnost" target="_blank" className="underline">politiko zasebnosti</Link>.</span>
        </label>
        <p className="text-xs text-[#6C6560] bg-[#F3ECE1] rounded-xl p-3">Po oddaji vaju kontaktiramo s podatki za plačilo. Galerijo za goste aktiviramo, ko je plačilo potrjeno. Do takrat lahko urejata nastavitve in pripravita QR kodo.</p>
        <button disabled={busy} className="btn-dark w-full h-12 disabled:opacity-50" data-testid="create-submit-button">{busy ? "Ustvarjanje …" : `Ustvari poroko${total !== null ? ` · ${total} €` : ""}`}</button>
      </form>
      <p className="text-sm text-[#6C6560]">Že imata račun? <Link to="/prijava" className="underline text-[#B39264]">Prijava</Link></p>
    </AuthShell>
  );
}
